import React, { useMemo } from "react";
import { useRacing } from "../context/RacingContext";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { BarChart3, TrendingDown } from "lucide-react";

export default function Analytics() {
  const { runs } = useRacing();

  const data = useMemo(() => {
    const sorted = [...runs].filter((r) => r.date).sort((a, b) => a.date.localeCompare(b.date));
    const etTrend = sorted
      .map((r) => ({ date: r.date.slice(5), et: parseFloat(r.et), rt: parseFloat(r.reactionTime) }))
      .filter((d) => !isNaN(d.et));
    const wins = runs.filter((r) => r.result === "win").length;
    const losses = runs.filter((r) => r.result === "loss").length;
    const byes = runs.filter((r) => r.result === "bye").length;

    const byTrack = {};
    runs.forEach((r) => {
      if (!r.track) return;
      byTrack[r.track] = (byTrack[r.track] || 0) + 1;
    });
    const trackData = Object.entries(byTrack).map(([track, count]) => ({ track, count })).sort((a, b) => b.count - a.count).slice(0, 6);

    const ets = etTrend.map((d) => d.et);
    const avgET = ets.length ? (ets.reduce((a, b) => a + b, 0) / ets.length).toFixed(3) : "—";
    const consistency = ets.length > 1 ? (Math.max(...ets) - Math.min(...ets)).toFixed(3) : "—";

    return { etTrend, wins, losses, byes, trackData, avgET, consistency };
  }, [runs]);

  if (runs.length === 0) {
    return (
      <div className="mx-auto max-w-4xl">
        <h1 className="mb-6 font-head text-3xl font-black tracking-tighter">Analytics</h1>
        <div className="rounded-xl border border-dashed border-mt-border bg-mt-card p-10 text-center text-mt-mutedfg">
          <BarChart3 size={32} className="mx-auto" />
          <p className="mt-3">Log some runs to unlock your trends and consistency charts.</p>
        </div>
      </div>
    );
  }

  const resultData = [
    { name: "Wins", value: data.wins, color: "#2DB45D" },
    { name: "Losses", value: data.losses, color: "#1A1A1E" },
    { name: "Byes", value: data.byes, color: "#F0A500" },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <h1 className="font-head text-3xl font-black tracking-tighter">Analytics</h1>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <MiniStat label="Avg ET" value={data.avgET} />
        <MiniStat label="ET Spread" value={data.consistency} hint="lower = more consistent" icon />
        <MiniStat label="Wins" value={data.wins} />
        <MiniStat label="Total Runs" value={runs.length} />
      </div>

      <Panel title="ET Trend" testid="chart-et-trend">
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data.etTrend} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DA" />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#86868B" }} />
            <YAxis tick={{ fontSize: 11, fill: "#86868B" }} domain={["auto", "auto"]} />
            <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #D4D0C4", fontFamily: "JetBrains Mono" }} />
            <Line type="monotone" dataKey="et" stroke="#0057FF" strokeWidth={2.5} dot={{ r: 3, fill: "#0057FF" }} isAnimationActive />
          </LineChart>
        </ResponsiveContainer>
      </Panel>

      <div className="grid gap-6 md:grid-cols-2">
        <Panel title="Reaction Time Trend" testid="chart-rt-trend">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.etTrend} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DA" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#86868B" }} />
              <YAxis tick={{ fontSize: 11, fill: "#86868B" }} domain={["auto", "auto"]} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #D4D0C4", fontFamily: "JetBrains Mono" }} />
              <Line type="monotone" dataKey="rt" stroke="#F0A500" strokeWidth={2.5} dot={{ r: 3, fill: "#F0A500" }} isAnimationActive />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Win / Loss Record" testid="chart-record">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={resultData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DA" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#86868B" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#86868B" }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #D4D0C4" }} cursor={{ fill: "#EDE9E0" }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]} isAnimationActive>
                {resultData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <Panel title="Runs by Track" testid="chart-tracks">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data.trackData} layout="vertical" margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DA" />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#86868B" }} />
            <YAxis type="category" dataKey="track" width={120} tick={{ fontSize: 11, fill: "#1A1A1E" }} />
            <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #D4D0C4" }} cursor={{ fill: "#EDE9E0" }} />
            <Bar dataKey="count" fill="#0057FF" radius={[0, 6, 6, 0]} isAnimationActive />
          </BarChart>
        </ResponsiveContainer>
      </Panel>
    </div>
  );
}

function MiniStat({ label, value, hint, icon }) {
  return (
    <div className="rounded-xl border border-mt-border bg-mt-card p-4 shadow-sm">
      <div className="flex items-center gap-1 text-xs font-bold uppercase tracking-[0.15em] text-mt-mutedfg">
        {icon && <TrendingDown size={12} />} {label}
      </div>
      <div className="mt-2 font-mono text-2xl font-bold">{value}</div>
      {hint && <div className="mt-1 text-[10px] text-mt-mutedfg">{hint}</div>}
    </div>
  );
}

function Panel({ title, children, testid }) {
  return (
    <div className="rounded-xl border border-mt-border bg-mt-card p-4 shadow-sm" data-testid={testid}>
      <h3 className="mb-4 font-head text-sm font-bold uppercase tracking-[0.2em] text-mt-primary">{title}</h3>
      {children}
    </div>
  );
}
